import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

export type Vec3 = readonly [number, number, number];

const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _euler = new THREE.Euler();
const _scale = new THREE.Vector3();

/** Builds a TRS matrix (rotation order XYZ, applied after scale). */
export function trs(
  position: Vec3 = [0, 0, 0],
  rotation: Vec3 = [0, 0, 0],
  scale: Vec3 | number = 1,
) {
  const s = typeof scale === 'number' ? [scale, scale, scale] : scale;
  return new THREE.Matrix4().compose(
    _pos.set(position[0], position[1], position[2]),
    _quat.setFromEuler(_euler.set(rotation[0], rotation[1], rotation[2])),
    _scale.set(s[0] as number, s[1] as number, s[2] as number),
  );
}

/** `parent × child`, without mutating either. */
export function mul(parent: THREE.Matrix4, ...children: THREE.Matrix4[]): THREE.Matrix4 {
  const out = parent.clone();
  for (const child of children) out.multiply(child);
  return out;
}

export interface PartOptions {
  /** Second colour for a gradient along `axis` of the template geometry. */
  color2?: THREE.ColorRepresentation;
  axis?: 'x' | 'y' | 'z';
  /** Puts the part into the separate glow geometry. */
  glow?: boolean;
}

const _a = new THREE.Color();
const _b = new THREE.Color();
const _c = new THREE.Color();

/**
 * Collects coloured, transformed copies of template geometries and merges them
 * into a single vertex-coloured BufferGeometry (one draw call per model).
 */
export class GeometryBuilder {
  private readonly parts: THREE.BufferGeometry[] = [];
  private readonly glowParts: THREE.BufferGeometry[] = [];

  add(
    template: THREE.BufferGeometry,
    color: THREE.ColorRepresentation,
    matrix: THREE.Matrix4,
    options: PartOptions = {},
  ): void {
    const geometry = template.index ? template.toNonIndexed() : template.clone();
    for (const name of Object.keys(geometry.attributes)) {
      if (name !== 'position' && name !== 'normal') geometry.deleteAttribute(name);
    }

    const position = geometry.getAttribute('position');
    const axisIndex = options.axis === 'x' ? 0 : options.axis === 'z' ? 2 : 1;
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < position.count; i++) {
      const v = position.getComponent(i, axisIndex);
      if (v < min) min = v;
      if (v > max) max = v;
    }
    _a.set(color);
    _b.set(options.color2 ?? color);
    const colors = new Float32Array(position.count * 3);
    for (let i = 0; i < position.count; i++) {
      const t = max > min ? (position.getComponent(i, axisIndex) - min) / (max - min) : 0;
      _c.copy(_a).lerp(_b, t);
      colors[i * 3] = _c.r;
      colors[i * 3 + 1] = _c.g;
      colors[i * 3 + 2] = _c.b;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.applyMatrix4(matrix);
    (options.glow ? this.glowParts : this.parts).push(geometry);
  }

  /** Adds a geometry that already has position/normal/color attributes. Takes ownership. */
  addPrepared(geometry: THREE.BufferGeometry, glow = false): void {
    (glow ? this.glowParts : this.parts).push(geometry);
  }

  private static merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry | null {
    if (parts.length === 0) return null;
    const merged = mergeGeometries(parts, false);
    for (const part of parts) part.dispose();
    if (!merged) return null;
    merged.computeBoundingSphere();
    return merged;
  }

  build(): { base: THREE.BufferGeometry; glow: THREE.BufferGeometry | null } {
    const base = GeometryBuilder.merge(this.parts) ?? new THREE.BufferGeometry();
    return { base, glow: GeometryBuilder.merge(this.glowParts) };
  }
}

/** Shared unit-sized template geometries. Never dispose these. */
export const Templates = {
  sphere: new THREE.SphereGeometry(1, 10, 8),
  sphereLow: new THREE.SphereGeometry(1, 7, 5),
  /** Radius 1, height 1, base at y = 0. */
  cylinder: new THREE.CylinderGeometry(1, 1, 1, 7, 1).translate(0, 0.5, 0),
  /** Radius 1, height 1, base at y = 0. */
  cone: new THREE.ConeGeometry(1, 1, 8, 1).translate(0, 0.5, 0),
  ico: new THREE.IcosahedronGeometry(1, 0),
  ico1: new THREE.IcosahedronGeometry(1, 1),
  box: new THREE.BoxGeometry(1, 1, 1),
  bell: new THREE.LatheGeometry(
    [
      new THREE.Vector2(0.0, 0.0),
      new THREE.Vector2(0.35, 0.05),
      new THREE.Vector2(0.55, 0.25),
      new THREE.Vector2(0.75, 0.55),
      new THREE.Vector2(1.0, 0.85),
      new THREE.Vector2(0.95, 1.0),
    ].map((v) => new THREE.Vector2(v.x, -v.y)),
    10,
  ),
  star: (() => {
    const shape = new THREE.Shape();
    const points = 5;
    for (let i = 0; i <= points * 2; i++) {
      const r = i % 2 === 0 ? 1 : 0.45;
      const a = (i / (points * 2)) * Math.PI * 2 + Math.PI / 2;
      const x = Math.cos(a) * r;
      const y = Math.sin(a) * r;
      if (i === 0) shape.moveTo(x, y);
      else shape.lineTo(x, y);
    }
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: 0.15,
      bevelEnabled: true,
      bevelThickness: 0.12,
      bevelSize: 0.1,
      bevelSegments: 1,
    });
    // Lay it flat, facing +y.
    geometry.rotateX(-Math.PI / 2);
    geometry.center();
    return geometry;
  })(),
};
